import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, Briefcase, CalendarCheck, MapPin, Clock, TrendingUp, ShieldCheck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ASSETS, NEARBY_JOBS } from '../mock/data';

const StaffDashboard = () => {
  const nav = useNavigate();
  const { bookings, users, categories } = useApp();

  const handleLogout = () => {
    localStorage.removeItem('c24_staff');
    nav('/staff/login');
  };

  const cleaners = users.filter((u) => u.role === 'cleaner');
  const customers = users.filter((u) => u.role === 'customer');

  const stats = [
    { label: 'Active cleaners', value: cleaners.length || 12, icon: Users, color: '#2E7D32', bg: '#E8F5E9' },
    { label: 'Today\'s jobs', value: bookings.length || 8, icon: Briefcase, color: '#1E88E5', bg: '#E3F2FD' },
    { label: 'On-time %', value: '92%', icon: Clock, color: '#EA580C', bg: '#FFF3E0' },
    { label: 'Attendance', value: '96%', icon: CalendarCheck, color: '#7B1FA2', bg: '#F3E5F5' },
  ];

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="bg-[#1E88E5] text-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-6 py-4 flex items-center gap-4">
          <div className="bg-white rounded p-1">
            <img src={ASSETS.logo} alt="Clean24" className="h-10" />
          </div>
          <div>
            <p className="font-bold">Staff / Supervisor Portal</p>
            <p className="text-xs opacity-90">Ernakulam operations</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => nav('/')} className="text-xs bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition-colors">View site</button>
            <button onClick={handleLogout} className="inline-flex items-center gap-1 text-xs bg-white/15 hover:bg-red-500/40 px-3 py-1.5 rounded-lg transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-5 md:px-6 py-6 md:py-8">
        <h1 className="text-2xl md:text-3xl font-extrabold text-neutral-900">Dashboard</h1>
        <p className="text-sm text-neutral-600 mt-0.5">Monitor cleaners, jobs, and attendance in your zone.</p>

        {/* Stats */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: s.bg }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <p className="mt-3 text-xs text-neutral-500 font-medium">{s.label}</p>
              <p className="font-extrabold text-2xl mt-0.5">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {/* Live jobs */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold">Live jobs in zone</h2>
              <TrendingUp className="w-4 h-4 text-[#2E7D32]" />
            </div>
            <div className="space-y-3">
              {NEARBY_JOBS.map((j) => (
                <div key={j.id} className="flex items-start gap-3 p-3 rounded-lg border border-neutral-100 hover:bg-neutral-50">
                  <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4 text-[#2E7D32]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{j.service}</p>
                    <p className="text-xs text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.address}</p>
                    <p className="text-xs text-neutral-500">{j.time} • {j.distance}</p>
                  </div>
                  <span className="text-xs font-bold text-[#2E7D32] bg-[#E8F5E9] px-2 py-1 rounded-full">Live</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cleaners */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold">Cleaners on duty</h2>
              <button className="text-xs text-[#1E88E5] font-semibold hover:underline">View all</button>
            </div>
            {cleaners.length === 0 ? (
              <div className="py-8 text-center">
                <Users className="w-10 h-10 mx-auto text-neutral-300" />
                <p className="mt-2 text-sm text-neutral-500">No cleaners registered yet</p>
                <p className="text-xs text-neutral-400">Cleaners appear here after they sign up.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cleaners.map((c) => (
                  <div key={c.email} className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-50">
                    <div className="w-9 h-9 rounded-full bg-[#E3F2FD] flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#1E88E5]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{c.name}</p>
                      <p className="text-xs text-neutral-500 truncate">{c.email}</p>
                    </div>
                    <span className="text-[10px] font-bold text-[#2E7D32] bg-[#E8F5E9] px-2 py-0.5 rounded-full">Online</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Service coverage */}
          <div className="md:col-span-2 bg-white border border-neutral-200 rounded-xl p-5">
            <h2 className="font-bold mb-3">Service categories in your zone</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories.slice(0, 8).map((c) => (
                <div key={c.id} className="border border-neutral-200 rounded-lg overflow-hidden">
                  <img src={c.image} alt={c.name} className="w-full h-20 object-cover" />
                  <div className="p-2">
                    <p className="text-xs font-semibold truncate">{c.name}</p>
                    <p className="text-[10px] text-neutral-500">from ₹{c.price}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 bg-white border border-neutral-200 rounded-xl p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#1E88E5] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-neutral-600">
            Staff access is limited to viewing operations and monitoring cleaners. To edit categories, services, or offers,
            please use the <button onClick={() => nav('/admin/login')} className="text-[#2E7D32] font-semibold hover:underline">Super Admin panel</button>.
          </p>
        </div>
      </main>
    </div>
  );
};

export default StaffDashboard;
